import { useEffect, useMemo, useState } from "react"
import {
  faArrowsRotate,
  faClose,
  faEye,
  faMagnifyingGlass,
  faPalette,
  faShareNodes,
  faSliders,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { useTranslation } from "react-i18next"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { SIZE } from "../../../configuration/Sizes"
import { companyMemory } from "../../../Objects/Memory/Memory"
import { CanView } from "../../../components/CanView/CanView"
import { Permissions } from "../../../Enum/PermissionsType"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import MenuCard from "../../../PublicMenu/Components/MenuCard"
import { EOrderPlaceType } from "../../../Enum/EOrderPlaceType"
import {
  getPublicMenuTheme,
  isLegacyPublicMenuTheme,
  LEGACY_PUBLIC_MENU_THEME,
  PublicMenuCardLayout,
} from "../../../PublicMenu/Helpers/PublicMenuTheme"
import { IProductRequest } from "../../../Interfaces/IProductRequest"
import { ColorField } from "../../../components/ColorField/ColorField"
import { normalizeHexColor } from "../../../Objects/Generics/Helps"
import { API_ROUTES } from "../../../configuration/RoutesApi"
import { SYSTEM_PUBLIC_MENU_THEMES } from "../../../PublicMenu/Helpers/PublicMenuThemePresets"
import { publicMenuThemeFromDesign } from "../../../Objects/Company/PublicMenuThemeEntity"
import { usePublicMenuThemes } from "../../../hooks/Company/usePublicMenuThemes"
import { PublicMenuThemeNamePopup } from "./PublicMenuThemeNamePopup"
import { useSaveCompany } from "../../../hooks/Company/useSaveCompany"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"

interface PublicMenuCustomizationPopupProps {
  onClose: () => void
}

export const PublicMenuCustomizationPopup = ({ onClose }: PublicMenuCustomizationPopupProps) => {
  const { t } = useTranslation("company")

  const canEdite = !usePermission(Permissions.CanEditeCompany)

  const { themes: customThemes, loadThemes, saveTheme } = usePublicMenuThemes()

  const { handleSavePublicMenuDesign } = useSaveCompany()

  const [selectedThemeKey, setSelectedThemeKey] = useState("current")

  const [isThemeNamePopupOpen, setThemeNamePopupOpen] = useState(false)

  const initialTheme = useMemo(() => getPublicMenuTheme(companyMemory), [])

  const [cardLayout, setCardLayout] = useState<PublicMenuCardLayout>(initialTheme.cardLayout)

  const [primaryColor, setPrimaryColor] = useState(initialTheme.primaryColor)

  const [secondaryColor, setSecondaryColor] = useState(initialTheme.secondaryColor)

  const [accentColor, setAccentColor] = useState(initialTheme.accentColor)

  const [backgroundColor, setBackgroundColor] = useState(initialTheme.backgroundColor)

  const [surfaceColor, setSurfaceColor] = useState(initialTheme.surfaceColor)

  const [textColor, setTextColor] = useState(initialTheme.textColor)

  const [borderColor, setBorderColor] = useState(initialTheme.borderColor)

  const [buttonColor, setButtonColor] = useState(initialTheme.buttonColor)

  const [buttonTextColor, setButtonTextColor] = useState(initialTheme.buttonTextColor)

  const [searchBackgroundColor, setSearchBackgroundColor] = useState(initialTheme.searchBackgroundColor)

  const [searchBorderColor, setSearchBorderColor] = useState(initialTheme.searchBorderColor)

  const [mobileNavBackgroundColor, setMobileNavBackgroundColor] = useState(initialTheme.mobileNavBackgroundColor)

  const [mobileNavActiveBackgroundColor, setMobileNavActiveBackgroundColor] = useState(
    initialTheme.mobileNavActiveBackgroundColor,
  )

  const [mobileNavActiveTextColor, setMobileNavActiveTextColor] = useState(initialTheme.mobileNavActiveTextColor)

  const [mobileNavActiveIconColor, setMobileNavActiveIconColor] = useState(initialTheme.mobileNavActiveIconColor)

  const [mobileNavInactiveBackgroundColor, setMobileNavInactiveBackgroundColor] = useState(
    initialTheme.mobileNavInactiveBackgroundColor,
  )

  const [mobileNavInactiveTextColor, setMobileNavInactiveTextColor] = useState(initialTheme.mobileNavInactiveTextColor)

  const [mobileNavInactiveIconColor, setMobileNavInactiveIconColor] = useState(initialTheme.mobileNavInactiveIconColor)

  const [headerBackgroundColor, setHeaderBackgroundColor] = useState(initialTheme.headerBackgroundColor)

  const [restaurantNameColor, setRestaurantNameColor] = useState(initialTheme.restaurantNameColor)

  const [restaurantDescriptionColor, setRestaurantDescriptionColor] = useState(initialTheme.restaurantDescriptionColor)

  const [statusCardBackgroundColor, setStatusCardBackgroundColor] = useState(initialTheme.statusCardBackgroundColor)

  const [cartButtonColor, setCartButtonColor] = useState(initialTheme.cartButtonColor)

  const [cartBackgroundColor, setCartBackgroundColor] = useState(initialTheme.cartBackgroundColor)

  const [cartBorderColor, setCartBorderColor] = useState(initialTheme.cartBorderColor)

  const [cartTitleTextColor, setCartTitleTextColor] = useState(initialTheme.cartTitleTextColor)

  const [cartDescriptionTextColor, setCartDescriptionTextColor] = useState(initialTheme.cartDescriptionTextColor)

  const [cartTotalLabelTextColor, setCartTotalLabelTextColor] = useState(initialTheme.cartTotalLabelTextColor)

  const [cartTotalValueTextColor, setCartTotalValueTextColor] = useState(initialTheme.cartTotalValueTextColor)

  const [emptyStateTitleColor, setEmptyStateTitleColor] = useState(initialTheme.emptyStateTitleColor)

  const [emptyStateDescriptionColor, setEmptyStateDescriptionColor] = useState(initialTheme.emptyStateDescriptionColor)

  const [categoryBackgroundColor, setCategoryBackgroundColor] = useState(initialTheme.categoryBackgroundColor)

  const [categoryBorderColor, setCategoryBorderColor] = useState(initialTheme.categoryBorderColor)

  const [categoryDividerColor, setCategoryDividerColor] = useState(initialTheme.categoryDividerColor)

  const [loginButtonBackgroundColor, setLoginButtonBackgroundColor] = useState(initialTheme.loginButtonBackgroundColor)

  const [loginButtonTextColor, setLoginButtonTextColor] = useState(initialTheme.loginButtonTextColor)

  const [shareButtonBackgroundColor, setShareButtonBackgroundColor] = useState(initialTheme.shareButtonBackgroundColor)

  const [shareButtonTextColor, setShareButtonTextColor] = useState(initialTheme.shareButtonTextColor)

  const [serviceSelectorBackgroundColor, setServiceSelectorBackgroundColor] = useState(
    initialTheme.serviceSelectorBackgroundColor,
  )

  const [serviceSelectorTextColor, setServiceSelectorTextColor] = useState(initialTheme.serviceSelectorTextColor)

  const [serviceSelectorActiveBackgroundColor, setServiceSelectorActiveBackgroundColor] = useState(
    initialTheme.serviceSelectorActiveBackgroundColor,
  )

  const [serviceSelectorActiveTextColor, setServiceSelectorActiveTextColor] = useState(
    initialTheme.serviceSelectorActiveTextColor,
  )

  const [borderThickness, setBorderThickness] = useState(initialTheme.borderThickness)

  const [highlightPromotions, setHighlightPromotions] = useState(initialTheme.highlightPromotions)

  const [compactCategories, setCompactCategories] = useState(initialTheme.compactCategories)

  const [useGradientHeader, setUseGradientHeader] = useState(initialTheme.useGradientHeader)

  const [showAwardsTab, setShowAwardsTab] = useState(companyMemory.publicMenuShowAwardsTab ?? true)

  const [showPromotionsTab, setShowPromotionsTab] = useState(companyMemory.publicMenuShowPromotionsTab ?? true)

  const [showExtraServicesTab, setShowExtraServicesTab] = useState(companyMemory.publicMenuShowExtraServicesTab ?? true)

  const applyThemeState = (theme = LEGACY_PUBLIC_MENU_THEME) => {
    setCardLayout(theme.cardLayout)
    setPrimaryColor(theme.primaryColor)
    setSecondaryColor(theme.secondaryColor)
    setAccentColor(theme.accentColor)
    setBackgroundColor(theme.backgroundColor)
    setSurfaceColor(theme.surfaceColor)
    setTextColor(theme.textColor)
    setBorderColor(theme.borderColor)
    setButtonColor(theme.buttonColor)
    setButtonTextColor(theme.buttonTextColor)
    setSearchBackgroundColor(theme.searchBackgroundColor)
    setSearchBorderColor(theme.searchBorderColor)
    setMobileNavBackgroundColor(theme.mobileNavBackgroundColor)
    setMobileNavActiveBackgroundColor(theme.mobileNavActiveBackgroundColor)
    setMobileNavActiveTextColor(theme.mobileNavActiveTextColor)
    setMobileNavActiveIconColor(theme.mobileNavActiveIconColor)
    setMobileNavInactiveBackgroundColor(theme.mobileNavInactiveBackgroundColor)
    setMobileNavInactiveTextColor(theme.mobileNavInactiveTextColor)
    setMobileNavInactiveIconColor(theme.mobileNavInactiveIconColor)
    setHeaderBackgroundColor(theme.headerBackgroundColor)
    setRestaurantNameColor(theme.restaurantNameColor)
    setRestaurantDescriptionColor(theme.restaurantDescriptionColor)
    setStatusCardBackgroundColor(theme.statusCardBackgroundColor)
    setCartButtonColor(theme.cartButtonColor)
    setCartBackgroundColor(theme.cartBackgroundColor)
    setCartBorderColor(theme.cartBorderColor)
    setCartTitleTextColor(theme.cartTitleTextColor)
    setCartDescriptionTextColor(theme.cartDescriptionTextColor)
    setCartTotalLabelTextColor(theme.cartTotalLabelTextColor)
    setCartTotalValueTextColor(theme.cartTotalValueTextColor)
    setEmptyStateTitleColor(theme.emptyStateTitleColor)
    setEmptyStateDescriptionColor(theme.emptyStateDescriptionColor)
    setCategoryBackgroundColor(theme.categoryBackgroundColor)
    setCategoryBorderColor(theme.categoryBorderColor)
    setCategoryDividerColor(theme.categoryDividerColor)
    setLoginButtonBackgroundColor(theme.loginButtonBackgroundColor)
    setLoginButtonTextColor(theme.loginButtonTextColor)
    setShareButtonBackgroundColor(theme.shareButtonBackgroundColor)
    setShareButtonTextColor(theme.shareButtonTextColor)
    setServiceSelectorBackgroundColor(theme.serviceSelectorBackgroundColor)
    setServiceSelectorTextColor(theme.serviceSelectorTextColor)
    setServiceSelectorActiveBackgroundColor(theme.serviceSelectorActiveBackgroundColor)
    setServiceSelectorActiveTextColor(theme.serviceSelectorActiveTextColor)
    setBorderThickness(theme.borderThickness)
    setHighlightPromotions(theme.highlightPromotions)
    setCompactCategories(theme.compactCategories)
    setUseGradientHeader(theme.useGradientHeader)
  }

  useEffect(() => {
    applyThemeState(getPublicMenuTheme(companyMemory))
    setShowAwardsTab(companyMemory.publicMenuShowAwardsTab ?? true)
    setShowPromotionsTab(companyMemory.publicMenuShowPromotionsTab ?? true)
    setShowExtraServicesTab(companyMemory.publicMenuShowExtraServicesTab ?? true)
    void loadThemes(companyMemory.id)
  }, [])

  const previewTheme = useMemo(
    () => ({
      cardLayout,
      primaryColor: normalizeHexColor(primaryColor, LEGACY_PUBLIC_MENU_THEME.primaryColor),
      secondaryColor: normalizeHexColor(secondaryColor, LEGACY_PUBLIC_MENU_THEME.secondaryColor),
      accentColor: normalizeHexColor(accentColor, LEGACY_PUBLIC_MENU_THEME.accentColor),
      backgroundColor: normalizeHexColor(backgroundColor, LEGACY_PUBLIC_MENU_THEME.backgroundColor),
      surfaceColor: normalizeHexColor(surfaceColor, LEGACY_PUBLIC_MENU_THEME.surfaceColor),
      textColor: normalizeHexColor(textColor, LEGACY_PUBLIC_MENU_THEME.textColor),
      borderColor: normalizeHexColor(borderColor, LEGACY_PUBLIC_MENU_THEME.borderColor),
      buttonColor: normalizeHexColor(buttonColor, LEGACY_PUBLIC_MENU_THEME.buttonColor),
      buttonTextColor: normalizeHexColor(buttonTextColor, LEGACY_PUBLIC_MENU_THEME.buttonTextColor),
      searchBackgroundColor: normalizeHexColor(searchBackgroundColor, LEGACY_PUBLIC_MENU_THEME.searchBackgroundColor),
      searchBorderColor: normalizeHexColor(searchBorderColor, LEGACY_PUBLIC_MENU_THEME.searchBorderColor),
      mobileNavBackgroundColor: normalizeHexColor(
        mobileNavBackgroundColor,
        LEGACY_PUBLIC_MENU_THEME.mobileNavBackgroundColor,
      ),
      mobileNavActiveBackgroundColor: normalizeHexColor(
        mobileNavActiveBackgroundColor,
        LEGACY_PUBLIC_MENU_THEME.mobileNavActiveBackgroundColor,
      ),
      mobileNavActiveTextColor: normalizeHexColor(
        mobileNavActiveTextColor,
        LEGACY_PUBLIC_MENU_THEME.mobileNavActiveTextColor,
      ),
      mobileNavActiveIconColor: normalizeHexColor(
        mobileNavActiveIconColor,
        LEGACY_PUBLIC_MENU_THEME.mobileNavActiveIconColor,
      ),
      mobileNavInactiveBackgroundColor: normalizeHexColor(
        mobileNavInactiveBackgroundColor,
        LEGACY_PUBLIC_MENU_THEME.mobileNavInactiveBackgroundColor,
      ),
      mobileNavInactiveTextColor: normalizeHexColor(
        mobileNavInactiveTextColor,
        LEGACY_PUBLIC_MENU_THEME.mobileNavInactiveTextColor,
      ),
      mobileNavInactiveIconColor: normalizeHexColor(
        mobileNavInactiveIconColor,
        LEGACY_PUBLIC_MENU_THEME.mobileNavInactiveIconColor,
      ),
      headerBackgroundColor: normalizeHexColor(headerBackgroundColor, LEGACY_PUBLIC_MENU_THEME.headerBackgroundColor),
      restaurantNameColor: normalizeHexColor(restaurantNameColor, LEGACY_PUBLIC_MENU_THEME.restaurantNameColor),
      restaurantDescriptionColor: normalizeHexColor(
        restaurantDescriptionColor,
        LEGACY_PUBLIC_MENU_THEME.restaurantDescriptionColor,
      ),
      statusCardBackgroundColor: normalizeHexColor(
        statusCardBackgroundColor,
        LEGACY_PUBLIC_MENU_THEME.statusCardBackgroundColor,
      ),
      cartButtonColor: normalizeHexColor(cartButtonColor, LEGACY_PUBLIC_MENU_THEME.cartButtonColor),
      cartBackgroundColor: normalizeHexColor(cartBackgroundColor, LEGACY_PUBLIC_MENU_THEME.cartBackgroundColor),
      cartBorderColor: normalizeHexColor(cartBorderColor, LEGACY_PUBLIC_MENU_THEME.cartBorderColor),
      cartTitleTextColor: normalizeHexColor(cartTitleTextColor, LEGACY_PUBLIC_MENU_THEME.cartTitleTextColor),
      cartDescriptionTextColor: normalizeHexColor(
        cartDescriptionTextColor,
        LEGACY_PUBLIC_MENU_THEME.cartDescriptionTextColor,
      ),
      cartTotalLabelTextColor: normalizeHexColor(
        cartTotalLabelTextColor,
        LEGACY_PUBLIC_MENU_THEME.cartTotalLabelTextColor,
      ),
      cartTotalValueTextColor: normalizeHexColor(
        cartTotalValueTextColor,
        LEGACY_PUBLIC_MENU_THEME.cartTotalValueTextColor,
      ),
      emptyStateTitleColor: normalizeHexColor(emptyStateTitleColor, LEGACY_PUBLIC_MENU_THEME.emptyStateTitleColor),
      emptyStateDescriptionColor: normalizeHexColor(
        emptyStateDescriptionColor,
        LEGACY_PUBLIC_MENU_THEME.emptyStateDescriptionColor,
      ),
      categoryBackgroundColor: normalizeHexColor(
        categoryBackgroundColor,
        LEGACY_PUBLIC_MENU_THEME.categoryBackgroundColor,
      ),
      categoryBorderColor: normalizeHexColor(categoryBorderColor, LEGACY_PUBLIC_MENU_THEME.categoryBorderColor),
      categoryDividerColor: normalizeHexColor(categoryDividerColor, LEGACY_PUBLIC_MENU_THEME.categoryDividerColor),
      loginButtonBackgroundColor: normalizeHexColor(
        loginButtonBackgroundColor,
        LEGACY_PUBLIC_MENU_THEME.loginButtonBackgroundColor,
      ),
      loginButtonTextColor: normalizeHexColor(loginButtonTextColor, LEGACY_PUBLIC_MENU_THEME.loginButtonTextColor),
      shareButtonBackgroundColor: normalizeHexColor(
        shareButtonBackgroundColor,
        LEGACY_PUBLIC_MENU_THEME.shareButtonBackgroundColor,
      ),
      shareButtonTextColor: normalizeHexColor(shareButtonTextColor, LEGACY_PUBLIC_MENU_THEME.shareButtonTextColor),
      serviceSelectorBackgroundColor: normalizeHexColor(
        serviceSelectorBackgroundColor,
        LEGACY_PUBLIC_MENU_THEME.serviceSelectorBackgroundColor,
      ),
      serviceSelectorTextColor: normalizeHexColor(
        serviceSelectorTextColor,
        LEGACY_PUBLIC_MENU_THEME.serviceSelectorTextColor,
      ),
      serviceSelectorActiveBackgroundColor: normalizeHexColor(
        serviceSelectorActiveBackgroundColor,
        LEGACY_PUBLIC_MENU_THEME.serviceSelectorActiveBackgroundColor,
      ),
      serviceSelectorActiveTextColor: normalizeHexColor(
        serviceSelectorActiveTextColor,
        LEGACY_PUBLIC_MENU_THEME.serviceSelectorActiveTextColor,
      ),
      borderThickness: Math.max(1, Math.min(4, Math.round(borderThickness))),
      highlightPromotions,
      compactCategories,
      useGradientHeader,
    }),
    [
      accentColor,
      backgroundColor,
      borderColor,
      borderThickness,
      buttonColor,
      buttonTextColor,
      cartBackgroundColor,
      cartBorderColor,
      cartButtonColor,
      cartDescriptionTextColor,
      cartTitleTextColor,
      cartTotalLabelTextColor,
      cartTotalValueTextColor,
      categoryBackgroundColor,
      categoryBorderColor,
      categoryDividerColor,
      cardLayout,
      compactCategories,
      emptyStateDescriptionColor,
      emptyStateTitleColor,
      headerBackgroundColor,
      highlightPromotions,
      loginButtonBackgroundColor,
      loginButtonTextColor,
      mobileNavActiveBackgroundColor,
      mobileNavActiveIconColor,
      mobileNavActiveTextColor,
      mobileNavBackgroundColor,
      mobileNavInactiveBackgroundColor,
      mobileNavInactiveIconColor,
      mobileNavInactiveTextColor,
      primaryColor,
      searchBackgroundColor,
      searchBorderColor,
      secondaryColor,
      serviceSelectorActiveBackgroundColor,
      serviceSelectorActiveTextColor,
      serviceSelectorBackgroundColor,
      serviceSelectorTextColor,
      shareButtonBackgroundColor,
      shareButtonTextColor,
      statusCardBackgroundColor,
      surfaceColor,
      restaurantDescriptionColor,
      restaurantNameColor,
      textColor,
      useGradientHeader,
    ],
  )

  const isLegacyPreview = isLegacyPublicMenuTheme(previewTheme)

  const previewProduct: IProductRequest = useMemo(
    () => ({
      id: "preview-burger",
      name: t("public_menu_customization.preview_product.name"),
      description: t("public_menu_customization.preview_product.description"),
      price: 29.9,
      oldPrice: 35.9,
      categoryId: 1,
      categoryIcon: "🍔",
      image: "/public/foodEx.png",
      badges: [t("public_menu_customization.preview_product.badge")],
      availableAddOns: [],
    }),
    [t],
  )

  const handleRestoreDefault = () => {
    applyThemeState(LEGACY_PUBLIC_MENU_THEME)
    setShowAwardsTab(true)
    setShowPromotionsTab(true)
    setShowExtraServicesTab(true)
    setSelectedThemeKey("system:system-default")
  }

  const getDesignPayload = () => ({
    publicMenuCardLayout: previewTheme.cardLayout,
    publicMenuPrimaryColor: previewTheme.primaryColor,
    publicMenuSecondaryColor: previewTheme.secondaryColor,
    publicMenuAccentColor: previewTheme.accentColor,
    publicMenuBackgroundColor: previewTheme.backgroundColor,
    publicMenuSurfaceColor: previewTheme.surfaceColor,
    publicMenuTextColor: previewTheme.textColor,
    publicMenuBorderColor: previewTheme.borderColor,
    publicMenuButtonColor: previewTheme.buttonColor,
    publicMenuButtonTextColor: previewTheme.buttonTextColor,
    publicMenuSearchBackgroundColor: previewTheme.searchBackgroundColor,
    publicMenuSearchBorderColor: previewTheme.searchBorderColor,
    publicMenuMobileNavBackgroundColor: previewTheme.mobileNavBackgroundColor,
    publicMenuMobileNavActiveBackgroundColor: previewTheme.mobileNavActiveBackgroundColor,
    publicMenuMobileNavActiveTextColor: previewTheme.mobileNavActiveTextColor,
    publicMenuMobileNavActiveIconColor: previewTheme.mobileNavActiveIconColor,
    publicMenuMobileNavInactiveBackgroundColor: previewTheme.mobileNavInactiveBackgroundColor,
    publicMenuMobileNavInactiveTextColor: previewTheme.mobileNavInactiveTextColor,
    publicMenuMobileNavInactiveIconColor: previewTheme.mobileNavInactiveIconColor,
    publicMenuHeaderBackgroundColor: previewTheme.headerBackgroundColor,
    publicMenuRestaurantNameColor: previewTheme.restaurantNameColor,
    publicMenuRestaurantDescriptionColor: previewTheme.restaurantDescriptionColor,
    publicMenuStatusCardBackgroundColor: previewTheme.statusCardBackgroundColor,
    publicMenuCartButtonColor: previewTheme.cartButtonColor,
    publicMenuCartBackgroundColor: previewTheme.cartBackgroundColor,
    publicMenuCartBorderColor: previewTheme.cartBorderColor,
    publicMenuCartTitleTextColor: previewTheme.cartTitleTextColor,
    publicMenuCartDescriptionTextColor: previewTheme.cartDescriptionTextColor,
    publicMenuCartTotalLabelTextColor: previewTheme.cartTotalLabelTextColor,
    publicMenuCartTotalValueTextColor: previewTheme.cartTotalValueTextColor,
    publicMenuEmptyStateTitleColor: previewTheme.emptyStateTitleColor,
    publicMenuEmptyStateDescriptionColor: previewTheme.emptyStateDescriptionColor,
    publicMenuCategoryBackgroundColor: previewTheme.categoryBackgroundColor,
    publicMenuCategoryBorderColor: previewTheme.categoryBorderColor,
    publicMenuCategoryDividerColor: previewTheme.categoryDividerColor,
    publicMenuLoginButtonBackgroundColor: previewTheme.loginButtonBackgroundColor,
    publicMenuLoginButtonTextColor: previewTheme.loginButtonTextColor,
    publicMenuShareButtonBackgroundColor: previewTheme.shareButtonBackgroundColor,
    publicMenuShareButtonTextColor: previewTheme.shareButtonTextColor,
    publicMenuServiceSelectorBackgroundColor: previewTheme.serviceSelectorBackgroundColor,
    publicMenuServiceSelectorTextColor: previewTheme.serviceSelectorTextColor,
    publicMenuServiceSelectorActiveBackgroundColor: previewTheme.serviceSelectorActiveBackgroundColor,
    publicMenuServiceSelectorActiveTextColor: previewTheme.serviceSelectorActiveTextColor,
    publicMenuBorderThickness: previewTheme.borderThickness,
    publicMenuHighlightPromotions: previewTheme.highlightPromotions,
    publicMenuCompactCategories: previewTheme.compactCategories,
    publicMenuUseGradientHeader: previewTheme.useGradientHeader,
    publicMenuShowAwardsTab: showAwardsTab,
    publicMenuShowPromotionsTab: showPromotionsTab,
    publicMenuShowExtraServicesTab: showExtraServicesTab,
  })

  const handleTrySave = () => {
    const selectedSystemTheme = selectedThemeKey.startsWith("system:")
      ? SYSTEM_PUBLIC_MENU_THEMES.find((theme) => `system:${theme.id}` === selectedThemeKey)
      : undefined
    const selectedCustomTheme = selectedThemeKey.startsWith("custom:")
      ? customThemes.find((theme) => theme.id === Number(selectedThemeKey.replace("custom:", "")))
      : undefined

    if (
      selectedSystemTheme &&
      JSON.stringify(previewTheme) === JSON.stringify(selectedSystemTheme.theme) &&
      showAwardsTab &&
      showPromotionsTab &&
      showExtraServicesTab
    ) {
      void handleSavePublicMenuDesign(companyMemory.id, getDesignPayload(), () => {})
      return
    }

    if (selectedCustomTheme) {
      void saveTheme(companyMemory.id, selectedCustomTheme.name, getDesignPayload(), selectedCustomTheme.id)
      return
    }

    setThemeNamePopupOpen(true)
  }

  const layoutOptions: Array<{ key: PublicMenuCardLayout; title: string; description: string }> = [
    {
      key: "cover",
      title: t("public_menu_customization.layout.cover.title"),
      description: t("public_menu_customization.layout.cover.description"),
    },
    {
      key: "compact-left",
      title: t("public_menu_customization.layout.compact_left.title"),
      description: t("public_menu_customization.layout.compact_left.description"),
    },
    {
      key: "compact-right",
      title: t("public_menu_customization.layout.compact_right.title"),
      description: t("public_menu_customization.layout.compact_right.description"),
    },
  ]

  const themeDropdownOptions = [
    {
      label: t("public_menu_customization.themes.current_store_theme"),
      value: "current",
    },
    ...SYSTEM_PUBLIC_MENU_THEMES.map((preset) => ({
      label: `${t("public_menu_customization.themes.system_option_prefix")} - ${t(preset.nameKey)}`,
      value: `system:${preset.id}`,
    })),
    ...customThemes.map((theme) => ({
      label: `${t("public_menu_customization.themes.custom_option_prefix")} - ${theme.name}`,
      value: `custom:${theme.id}`,
    })),
  ]

  const handleThemeSelection = (key: string) => {
    setSelectedThemeKey(key)

    if (key === "current") {
      applyThemeState(getPublicMenuTheme(companyMemory))
      return
    }

    if (key.startsWith("system:")) {
      const preset = SYSTEM_PUBLIC_MENU_THEMES.find((item) => `system:${item.id}` === key)
      if (preset) {
        applyThemeState(preset.theme)
        setShowAwardsTab(true)
        setShowPromotionsTab(true)
        setShowExtraServicesTab(true)
      }
      return
    }

    const customTheme = customThemes.find((item) => `custom:${item.id}` === key)
    if (customTheme) {
      applyThemeState(publicMenuThemeFromDesign(customTheme.design))
      setShowAwardsTab(Boolean(customTheme.design.publicMenuShowAwardsTab))
      setShowPromotionsTab(Boolean(customTheme.design.publicMenuShowPromotionsTab))
      setShowExtraServicesTab(Boolean(customTheme.design.publicMenuShowExtraServicesTab))
    }
  }

  return (
    <>
      <ModalPortal>
        <div className={defaultStyle.overlay}>
          <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
            <div className={defaultStyle.alert2}>
              <TitlePageResponsiveIconSmall
                title={t("label_public_menu_customization")}
                iconColor={THEME.colors.iconBGColor}
                icon={faPalette}
                children={
                  <div className="flex items-center gap-1">
                    <PopupHelpButton
                      helpKey="pages.myrestaurant.mycompany.publicmenucustomizationpopup.1"
                      variant="global"
                      title={t("helpPopup:global.menuCustomization.title")}
                      description={t("helpPopup:global.menuCustomization.description")}
                      bullets={t("helpPopup:global.menuCustomization.bullets", { returnObjects: true }) as string[]}
                    />

                    <ButtonIcon
                      sizeNumber={SIZE.buttonCloseIconSize}
                      bgColor={THEME.colors.buttonBGCloseColor}
                      iconColor={THEME.colors.buttonFGCloseColor}
                      icon={faClose}
                      onClick={onClose}
                      size="small"
                      descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.publicMenuCustomizationPopup.button.onClose"
                    />
                  </div>
                }
              />

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                <div className="min-h-0 overflow-y-auto p-2 ">
                  <form
                    className="space-y-2"
                    onSubmit={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleTrySave()
                    }}
                  >
                    <Animate type={AnimationTypes.fadeInTraslate} duration="1.8s">
                      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                        <div className="mb-4 flex items-center gap-3">
                          <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-violet-50 text-violet-500">
                            <FontAwesomeIcon icon={faPalette} />
                          </span>
                          <div>
                            <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-700">
                              {t("public_menu_customization.themes.title")}
                            </p>
                            <p className="text-sm text-slate-500">
                              {t("public_menu_customization.themes.description")}
                            </p>
                          </div>
                        </div>

                        <div className="grid gap-3 md:grid-cols-[1fr_auto]">
                          <GenericDropDown
                            disable={canEdite}
                            initialState={selectedThemeKey}
                            data={themeDropdownOptions}
                            handlerFuncion={(event) => handleThemeSelection(event.target.value)}
                            descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.publicMenuCustomizationPopup.select.select"
                          />

                          <button
                            type="button"
                            disabled={canEdite}
                            onClick={() => setThemeNamePopupOpen(true)}
                            className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm font-black text-violet-700 transition hover:bg-violet-100 disabled:opacity-50"
                            data-interaction-description-key="interactionDescriptions:pages.myRestaurant.myCompany.publicMenuCustomizationPopup.button.setThemeNamePopupOpenTrue"
                          >
                            {t("public_menu_customization.themes.save_as_new")}
                          </button>
                        </div>
                      </section>
                    </Animate>

                    <Animate type={AnimationTypes.fadeInTraslate} duration="1.8s">
                      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                        <div className="mb-4 flex items-center gap-3">
                          <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-50 text-orange-500">
                            <FontAwesomeIcon icon={faSliders} />
                          </span>

                          <div>
                            <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-700">
                              {t("public_menu_customization.list_layout_title")}
                            </p>

                            <p className="text-sm text-slate-500">
                              {t("public_menu_customization.list_layout_description")}
                            </p>
                          </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-3">
                          {layoutOptions.map((option) => {
                            const active = cardLayout === option.key

                            return (
                              <button
                                key={option.key}
                                type="button"
                                disabled={canEdite}
                                onClick={() => setCardLayout(option.key)}
                                className={`rounded-2xl border p-4 text-left transition ${
                                  active
                                    ? "border-orange-300 bg-orange-50"
                                    : "border-slate-200 bg-white hover:border-orange-200"
                                } disabled:cursor-not-allowed disabled:opacity-70`}
                                data-interaction-description-key="interactionDescriptions:pages.myRestaurant.myCompany.publicMenuCustomizationPopup.button.setCardLayoutOptionKey"
                              >
                                <div className="mb-3 rounded-[0.9rem] border border-dashed border-slate-200 bg-slate-50 p-3">
                                  {option.key === "cover" ? (
                                    <div
                                      className="h-14 rounded-xl"
                                      style={{
                                        background: `linear-gradient(135deg, ${previewTheme.primaryColor}, ${previewTheme.accentColor})`,
                                      }}
                                    />
                                  ) : (
                                    <div
                                      className={`flex items-center gap-2 ${option.key === "compact-right" ? "flex-row-reverse" : ""}`}
                                    >
                                      <div
                                        className="h-10 w-10 rounded-xl"
                                        style={{
                                          background: `linear-gradient(135deg, ${previewTheme.primaryColor}, ${previewTheme.accentColor})`,
                                        }}
                                      />
                                      <div className="flex-1 space-y-2">
                                        <div className="h-2 rounded-full bg-slate-300" />
                                        <div className="h-2 w-2/3 rounded-full bg-slate-200" />
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <p className="text-sm font-black text-slate-800">{option.title}</p>
                                <p className="mt-1 text-xs leading-relaxed text-slate-500">{option.description}</p>
                              </button>
                            )
                          })}
                        </div>
                      </section>
                    </Animate>

                    <Animate type={AnimationTypes.fadeInTraslate} duration="2s">
                      <div className="grid gap-4 sm:gap-6 lg:grid-cols-[1.08fr_0.92fr]">
                        <div className="space-y-4">
                          <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                            <div className="mb-4 flex items-center gap-3">
                              <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-50 text-orange-500">
                                <FontAwesomeIcon icon={faPalette} />
                              </span>

                              <div>
                                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-700">
                                  {t("public_menu_customization.colors_title")}
                                </p>

                                <p className="text-sm text-slate-500">
                                  {t("public_menu_customization.colors_description")}
                                </p>
                              </div>
                            </div>

                            <div className="grid gap-3 overflow-y-auto max-h-[1000px]">
                              <ColorField
                                label={t("public_menu_customization.colors.primary.label")}
                                value={primaryColor}
                                onChange={setPrimaryColor}
                                helper={t("public_menu_customization.colors.primary.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.secondary.label")}
                                value={secondaryColor}
                                onChange={setSecondaryColor}
                                helper={t("public_menu_customization.colors.secondary.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.accent.label")}
                                value={accentColor}
                                onChange={setAccentColor}
                                helper={t("public_menu_customization.colors.accent.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.background.label")}
                                value={backgroundColor}
                                onChange={setBackgroundColor}
                                helper={t("public_menu_customization.colors.background.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.surface.label")}
                                value={surfaceColor}
                                onChange={setSurfaceColor}
                                helper={t("public_menu_customization.colors.surface.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.text.label")}
                                value={textColor}
                                onChange={setTextColor}
                                helper={t("public_menu_customization.colors.text.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.border.label")}
                                value={borderColor}
                                onChange={setBorderColor}
                                helper={t("public_menu_customization.colors.border.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.button.label")}
                                value={buttonColor}
                                onChange={setButtonColor}
                                helper={t("public_menu_customization.colors.button.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.button_text.label")}
                                value={buttonTextColor}
                                onChange={setButtonTextColor}
                                helper={t("public_menu_customization.colors.button_text.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.search_background.label")}
                                value={searchBackgroundColor}
                                onChange={setSearchBackgroundColor}
                                helper={t("public_menu_customization.colors.search_background.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.search_border.label")}
                                value={searchBorderColor}
                                onChange={setSearchBorderColor}
                                helper={t("public_menu_customization.colors.search_border.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.mobile_nav_background.label")}
                                value={mobileNavBackgroundColor}
                                onChange={setMobileNavBackgroundColor}
                                helper={t("public_menu_customization.colors.mobile_nav_background.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.mobile_nav_active_background.label")}
                                value={mobileNavActiveBackgroundColor}
                                onChange={setMobileNavActiveBackgroundColor}
                                helper={t("public_menu_customization.colors.mobile_nav_active_background.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.mobile_nav_active_text.label")}
                                value={mobileNavActiveTextColor}
                                onChange={setMobileNavActiveTextColor}
                                helper={t("public_menu_customization.colors.mobile_nav_active_text.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.mobile_nav_active_icon.label")}
                                value={mobileNavActiveIconColor}
                                onChange={setMobileNavActiveIconColor}
                                helper={t("public_menu_customization.colors.mobile_nav_active_icon.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.mobile_nav_inactive_background.label")}
                                value={mobileNavInactiveBackgroundColor}
                                onChange={setMobileNavInactiveBackgroundColor}
                                helper={t("public_menu_customization.colors.mobile_nav_inactive_background.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.mobile_nav_inactive_text.label")}
                                value={mobileNavInactiveTextColor}
                                onChange={setMobileNavInactiveTextColor}
                                helper={t("public_menu_customization.colors.mobile_nav_inactive_text.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.mobile_nav_inactive_icon.label")}
                                value={mobileNavInactiveIconColor}
                                onChange={setMobileNavInactiveIconColor}
                                helper={t("public_menu_customization.colors.mobile_nav_inactive_icon.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.header_background.label")}
                                value={headerBackgroundColor}
                                onChange={setHeaderBackgroundColor}
                                helper={t("public_menu_customization.colors.header_background.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.restaurant_name.label")}
                                value={restaurantNameColor}
                                onChange={setRestaurantNameColor}
                                helper={t("public_menu_customization.colors.restaurant_name.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.restaurant_description.label")}
                                value={restaurantDescriptionColor}
                                onChange={setRestaurantDescriptionColor}
                                helper={t("public_menu_customization.colors.restaurant_description.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.status_card_background.label")}
                                value={statusCardBackgroundColor}
                                onChange={setStatusCardBackgroundColor}
                                helper={t("public_menu_customization.colors.status_card_background.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.cart_button.label")}
                                value={cartButtonColor}
                                onChange={setCartButtonColor}
                                helper={t("public_menu_customization.colors.cart_button.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.cart_background.label")}
                                value={cartBackgroundColor}
                                onChange={setCartBackgroundColor}
                                helper={t("public_menu_customization.colors.cart_background.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.cart_border.label")}
                                value={cartBorderColor}
                                onChange={setCartBorderColor}
                                helper={t("public_menu_customization.colors.cart_border.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.cart_title_text.label")}
                                value={cartTitleTextColor}
                                onChange={setCartTitleTextColor}
                                helper={t("public_menu_customization.colors.cart_title_text.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.cart_description_text.label")}
                                value={cartDescriptionTextColor}
                                onChange={setCartDescriptionTextColor}
                                helper={t("public_menu_customization.colors.cart_description_text.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.cart_total_label_text.label")}
                                value={cartTotalLabelTextColor}
                                onChange={setCartTotalLabelTextColor}
                                helper={t("public_menu_customization.colors.cart_total_label_text.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.cart_total_value_text.label")}
                                value={cartTotalValueTextColor}
                                onChange={setCartTotalValueTextColor}
                                helper={t("public_menu_customization.colors.cart_total_value_text.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.empty_state_title.label")}
                                value={emptyStateTitleColor}
                                onChange={setEmptyStateTitleColor}
                                helper={t("public_menu_customization.colors.empty_state_title.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.empty_state_description.label")}
                                value={emptyStateDescriptionColor}
                                onChange={setEmptyStateDescriptionColor}
                                helper={t("public_menu_customization.colors.empty_state_description.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.category_background.label")}
                                value={categoryBackgroundColor}
                                onChange={setCategoryBackgroundColor}
                                helper={t("public_menu_customization.colors.category_background.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.category_border.label")}
                                value={categoryBorderColor}
                                onChange={setCategoryBorderColor}
                                helper={t("public_menu_customization.colors.category_border.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.category_divider.label")}
                                value={categoryDividerColor}
                                onChange={setCategoryDividerColor}
                                helper={t("public_menu_customization.colors.category_divider.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.login_button_background.label")}
                                value={loginButtonBackgroundColor}
                                onChange={setLoginButtonBackgroundColor}
                                helper={t("public_menu_customization.colors.login_button_background.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.login_button_text.label")}
                                value={loginButtonTextColor}
                                onChange={setLoginButtonTextColor}
                                helper={t("public_menu_customization.colors.login_button_text.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.share_button_background.label")}
                                value={shareButtonBackgroundColor}
                                onChange={setShareButtonBackgroundColor}
                                helper={t("public_menu_customization.colors.share_button_background.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.share_button_text.label")}
                                value={shareButtonTextColor}
                                onChange={setShareButtonTextColor}
                                helper={t("public_menu_customization.colors.share_button_text.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.service_selector_background.label")}
                                value={serviceSelectorBackgroundColor}
                                onChange={setServiceSelectorBackgroundColor}
                                helper={t("public_menu_customization.colors.service_selector_background.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.service_selector_text.label")}
                                value={serviceSelectorTextColor}
                                onChange={setServiceSelectorTextColor}
                                helper={t("public_menu_customization.colors.service_selector_text.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.service_selector_active_background.label")}
                                value={serviceSelectorActiveBackgroundColor}
                                onChange={setServiceSelectorActiveBackgroundColor}
                                helper={t("public_menu_customization.colors.service_selector_active_background.helper")}
                                disabled={canEdite}
                              />

                              <ColorField
                                label={t("public_menu_customization.colors.service_selector_active_text.label")}
                                value={serviceSelectorActiveTextColor}
                                onChange={setServiceSelectorActiveTextColor}
                                helper={t("public_menu_customization.colors.service_selector_active_text.helper")}
                                disabled={canEdite}
                              />
                            </div>
                          </section>
                        </div>

                        <aside className="w-full min-w-0 rounded-lg border border-slate-200 bg-slate-50 p-3 shadow-sm sm:p-2">
                          <div className="mb-4 flex items-center gap-2 text-sm font-black uppercase tracking-[0.16em] text-slate-700">
                            <FontAwesomeIcon icon={faEye} className="text-orange-500" />{" "}
                            {t("public_menu_customization.preview_real")}
                          </div>

                          <div className="flex justify-center">
                            <div
                              className="origin-top scale-1 overflow-hidden rounded-[1.2rem] sm:rounded-[1.35rem]"
                              style={{
                                backgroundColor: isLegacyPreview ? "#FFFFFF" : previewTheme.backgroundColor,
                              }}
                            >
                              <div
                                className="flex items-center justify-between gap-2 border-b px-3 py-2"
                                style={
                                  isLegacyPreview
                                    ? undefined
                                    : {
                                        backgroundColor: previewTheme.headerBackgroundColor,
                                        borderColor: previewTheme.borderColor,
                                      }
                                }
                              >
                                <div
                                  className="relative flex h-9 w-32 items-center overflow-hidden rounded-lg border p-1 text-[9px] font-black uppercase"
                                  style={
                                    isLegacyPreview
                                      ? undefined
                                      : {
                                          backgroundColor: previewTheme.serviceSelectorBackgroundColor,
                                          borderColor: previewTheme.borderColor,
                                          color: previewTheme.serviceSelectorTextColor,
                                        }
                                  }
                                >
                                  <span
                                    className="absolute inset-y-1 left-1 w-[calc(50%-4px)] rounded-md"
                                    style={{ backgroundColor: previewTheme.serviceSelectorActiveBackgroundColor }}
                                  />
                                  <span
                                    className="relative z-10 flex-1 text-center"
                                    style={{ color: previewTheme.serviceSelectorActiveTextColor }}
                                  >
                                    {t("publicMenu:navbar.delivery")}
                                  </span>
                                  <span
                                    className="relative z-10 flex-1 text-center"
                                    style={{ color: previewTheme.serviceSelectorTextColor }}
                                  >
                                    {t("publicMenu:navbar.table")}
                                  </span>
                                </div>

                                <div className="flex items-center gap-1.5">
                                  <button
                                    type="button"
                                    className="rounded-lg px-3 py-2 text-[9px] font-black uppercase"
                                    style={{
                                      backgroundColor: previewTheme.loginButtonBackgroundColor,
                                      color: previewTheme.loginButtonTextColor,
                                    }}
                                    data-interaction-description-key="interactionDescriptions:pages.myRestaurant.myCompany.publicMenuCustomizationPopup.button.title"
                                  >
                                    {t("publicMenu:login.title")}
                                  </button>
                                  <button
                                    type="button"
                                    className="flex h-8 w-8 items-center justify-center rounded-lg text-xs"
                                    style={{
                                      backgroundColor: previewTheme.shareButtonBackgroundColor,
                                      color: previewTheme.shareButtonTextColor,
                                    }}
                                    data-interaction-description-key="interactionDescriptions:pages.myRestaurant.myCompany.publicMenuCustomizationPopup.button.button"
                                  >
                                    <FontAwesomeIcon icon={faShareNodes} />
                                  </button>
                                </div>
                              </div>

                              <section
                                className={`${isLegacyPreview ? "border border-gray-100 bg-white" : "border"} mb-3 rounded-lg px-3 py-2 shadow-sm`}
                                style={
                                  isLegacyPreview
                                    ? undefined
                                    : {
                                        borderColor: previewTheme.secondaryColor,
                                        background: previewTheme.useGradientHeader
                                          ? `linear-gradient(135deg, ${previewTheme.headerBackgroundColor} 0%, ${previewTheme.surfaceColor} 55%, ${previewTheme.backgroundColor} 100%)`
                                          : previewTheme.headerBackgroundColor,
                                      }
                                }
                              >
                                <div className="flex items-center justify-between gap-3">
                                  <div className="flex min-w-0 items-center gap-2.5">
                                    <div className="h-10 w-10 rounded-md border border-gray-100 bg-white" />
                                    <div className="min-w-0">
                                      <h1
                                        className="truncate text-xs font-black uppercase tracking-wide text-gray-950"
                                        style={
                                          isLegacyPreview ? undefined : { color: previewTheme.restaurantNameColor }
                                        }
                                      >
                                        {companyMemory.companyName || t("public_menu_customization.your_restaurant")}
                                      </h1>
                                      <p
                                        className="truncate text-[10px] text-gray-500"
                                        style={
                                          isLegacyPreview
                                            ? undefined
                                            : { color: previewTheme.restaurantDescriptionColor }
                                        }
                                      >
                                        {t("public_menu_customization.preview_identical")}
                                      </p>
                                    </div>
                                  </div>

                                  <div
                                    className="flex items-center gap-1.5 rounded-md border-2 border-green-200 px-2 py-1"
                                    style={{ backgroundColor: previewTheme.statusCardBackgroundColor }}
                                  >
                                    <span className="text-[11px] leading-none">🛵</span>
                                    <p className="text-[10px] font-bold text-green-800">
                                      {t("public_menu_customization.delivery_active")}
                                    </p>
                                  </div>
                                </div>
                              </section>

                              <div className="space-y-3 p-3">
                                <div className="flex px-1 space-x-2 overflow-x-auto no-scrollbar py-1">
                                  {[
                                    t("public_menu_customization.categories.snacks"),
                                    t("public_menu_customization.categories.drinks"),
                                    t("public_menu_customization.categories.desserts"),
                                  ].map((label, index) => (
                                    <button
                                      key={label}
                                      type="button"
                                      className={`shrink-0 rounded-lg font-bold transition-all border ${
                                        isLegacyPreview
                                          ? index === 0
                                            ? "px-4 py-1 bg-white ring-2 ring-orange-300 text-black shadow-gray-200"
                                            : "px-4 py-1 bg-gray-100 border-gray-100 text-gray-500"
                                          : ""
                                      }`}
                                      style={
                                        isLegacyPreview
                                          ? undefined
                                          : {
                                              padding: previewTheme.compactCategories
                                                ? "0.35rem 0.75rem"
                                                : "0.5rem 1rem",
                                              backgroundColor:
                                                index === 0
                                                  ? previewTheme.primaryColor
                                                  : previewTheme.categoryBackgroundColor,
                                              borderColor:
                                                index === 0
                                                  ? previewTheme.primaryColor
                                                  : previewTheme.categoryBorderColor,
                                              borderWidth: previewTheme.borderThickness,
                                              color: index === 0 ? "#FFFFFF" : previewTheme.textColor,
                                            }
                                      }
                                      data-interaction-description-key="interactionDescriptions:pages.myRestaurant.myCompany.publicMenuCustomizationPopup.button.button2"
                                    >
                                      {index === 0 ? "🍔" : index === 1 ? "🥤" : "🍰"}{" "}
                                      <span className="ml-1">{label}</span>
                                    </button>
                                  ))}
                                </div>

                                <div
                                  className="flex items-center gap-2 rounded-xl border px-3 py-2 text-xs"
                                  style={
                                    isLegacyPreview
                                      ? undefined
                                      : {
                                          backgroundColor: previewTheme.searchBackgroundColor,
                                          borderColor: previewTheme.searchBorderColor,
                                          color: previewTheme.textColor,
                                        }
                                  }
                                >
                                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                                  <span className="opacity-70">{t("publicMenu:home.search_placeholder")}</span>
                                </div>

                                <div className="flex items-center gap-2 px-1">
                                  <span
                                    className="rounded-full border px-3 py-1 text-[10px] font-black uppercase"
                                    style={
                                      isLegacyPreview
                                        ? undefined
                                        : {
                                            backgroundColor: previewTheme.categoryBackgroundColor,
                                            borderColor: previewTheme.categoryBorderColor,
                                            color: previewTheme.textColor,
                                          }
                                    }
                                  >
                                    🍔 {t("public_menu_customization.categories.snacks")}
                                  </span>
                                  <div
                                    className="h-px flex-1"
                                    style={{
                                      background: `linear-gradient(to right, ${previewTheme.categoryDividerColor}, transparent)`,
                                    }}
                                  />
                                </div>

                                <div className="grid grid-cols-1 gap-4">
                                  <div
                                    style={{
                                      borderColor: previewTheme.borderColor,
                                      borderWidth: previewTheme.borderThickness,
                                    }}
                                    className="rounded-[1.15rem] border p-2"
                                  >
                                    <MenuCard
                                      product={previewProduct}
                                      onAddToCart={() => {}}
                                      mode={EOrderPlaceType.Delivery}
                                      isOpen={true}
                                      theme={previewTheme}
                                    />
                                  </div>
                                </div>

                                <div
                                  className="flex items-center justify-between rounded-lg border px-3 py-2"
                                  style={{
                                    backgroundColor: previewTheme.cartBackgroundColor,
                                    borderColor: previewTheme.cartBorderColor,
                                    color: previewTheme.cartTotalValueTextColor,
                                  }}
                                >
                                  <div className="flex items-center gap-2">
                                    <span
                                      className="flex h-8 w-8 items-center justify-center rounded-lg text-xs font-black text-white"
                                      style={{ backgroundColor: previewTheme.cartButtonColor }}
                                    >
                                      2
                                    </span>
                                    <div>
                                      <p
                                        className="text-[9px] font-black uppercase"
                                        style={{ color: previewTheme.cartTitleTextColor }}
                                      >
                                        {t("publicMenu:cart.title")}
                                      </p>
                                      <p
                                        className="text-[9px] font-black uppercase"
                                        style={{ color: previewTheme.cartDescriptionTextColor }}
                                      >
                                        {t("publicMenu:cart.view_items")}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="text-right leading-none">
                                    <p
                                      className="mb-1 text-[8px] font-black uppercase"
                                      style={{ color: previewTheme.cartTotalLabelTextColor }}
                                    >
                                      {t("publicMenu:cart.total")}
                                    </p>
                                    <p
                                      className="text-sm font-black"
                                      style={{ color: previewTheme.cartTotalValueTextColor }}
                                    >
                                      R$ 59,80
                                    </p>
                                  </div>
                                </div>

                                <div
                                  className="rounded-lg border px-3 py-3 text-center"
                                  style={{ borderColor: previewTheme.borderColor }}
                                >
                                  <h3
                                    className="text-sm font-black"
                                    style={{ color: previewTheme.emptyStateTitleColor }}
                                  >
                                    {t("publicMenu:history.empty_title")}
                                  </h3>
                                  <p
                                    className="mt-1 text-[10px] font-medium"
                                    style={{ color: previewTheme.emptyStateDescriptionColor }}
                                  >
                                    {t("publicMenu:history.empty_description")}
                                  </p>
                                </div>

                                <div
                                  className="grid grid-cols-3 gap-1 rounded-lg border p-1"
                                  style={{
                                    backgroundColor: previewTheme.mobileNavBackgroundColor,
                                    borderColor: previewTheme.borderColor,
                                  }}
                                >
                                  {[
                                    { icon: "▤", label: t("publicMenu:navbar.menu"), active: true },
                                    { icon: "▣", label: t("publicMenu:navbar.orders"), active: false },
                                    { icon: "◷", label: t("publicMenu:navbar.history"), active: false },
                                  ].map(({ icon, label, active }) => (
                                    <div
                                      key={String(label)}
                                      className="flex flex-col items-center justify-center rounded-md px-1 py-1 text-[9px] font-black uppercase"
                                      style={{
                                        backgroundColor: active
                                          ? previewTheme.mobileNavActiveBackgroundColor
                                          : previewTheme.mobileNavInactiveBackgroundColor,
                                        color: active
                                          ? previewTheme.mobileNavActiveTextColor
                                          : previewTheme.mobileNavInactiveTextColor,
                                      }}
                                    >
                                      <span
                                        className="mb-0.5 flex h-5 w-5 items-center justify-center rounded-full"
                                        style={{
                                          color: active
                                            ? previewTheme.mobileNavActiveIconColor
                                            : previewTheme.mobileNavInactiveIconColor,
                                        }}
                                      >
                                        {icon}
                                      </span>
                                      {label}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </aside>
                      </div>
                    </Animate>

                    <Animate type={AnimationTypes.fadeInTraslate} duration="2.2s">
                      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                        <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-700">
                          {t("public_menu_customization.additional_settings")}
                        </p>

                        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
                          <div className="flex items-center justify-between gap-4">
                            <div>
                              <p className="text-sm font-black text-slate-800">
                                {t("public_menu_customization.border_thickness_title")}
                              </p>

                              <p className="mt-1 text-xs text-slate-500">
                                {t("public_menu_customization.border_thickness_description")}
                              </p>
                            </div>

                            <div className="min-w-[88px] rounded-xl border border-slate-200 bg-white px-3 py-2 text-center text-sm font-black text-slate-700">
                              {previewTheme.borderThickness}px
                            </div>
                          </div>

                          <input
                            type="range"
                            min={1}
                            max={4}
                            step={1}
                            disabled={canEdite}
                            value={borderThickness}
                            onChange={(e) => setBorderThickness(Number(e.target.value))}
                            className="mt-4 h-2 w-full cursor-pointer accent-orange-500 disabled:cursor-not-allowed"
                            data-interaction-description-key="interactionDescriptions:pages.myRestaurant.myCompany.publicMenuCustomizationPopup.input.input"
                          />
                        </div>

                        <div className="mt-4 grid gap-3">
                          {[
                            {
                              checked: highlightPromotions,
                              onChange: setHighlightPromotions,
                              title: t("public_menu_customization.toggles.highlight_promotions.title"),
                              description: t("public_menu_customization.toggles.highlight_promotions.description"),
                            },
                            {
                              checked: compactCategories,
                              onChange: setCompactCategories,
                              title: t("public_menu_customization.toggles.compact_categories.title"),
                              description: t("public_menu_customization.toggles.compact_categories.description"),
                            },
                            {
                              checked: useGradientHeader,
                              onChange: setUseGradientHeader,
                              title: t("public_menu_customization.toggles.gradient_header.title"),
                              description: t("public_menu_customization.toggles.gradient_header.description"),
                            },
                            {
                              checked: showAwardsTab,
                              onChange: setShowAwardsTab,
                              title: t("public_menu_customization.toggles.show_awards_tab.title"),
                              description: t("public_menu_customization.toggles.show_awards_tab.description"),
                            },
                            {
                              checked: showPromotionsTab,
                              onChange: setShowPromotionsTab,
                              title: t("public_menu_customization.toggles.show_promotions_tab.title"),
                              description: t("public_menu_customization.toggles.show_promotions_tab.description"),
                            },
                            {
                              checked: showExtraServicesTab,
                              onChange: setShowExtraServicesTab,
                              title: t("public_menu_customization.toggles.show_extra_services_tab.title"),
                              description: t("public_menu_customization.toggles.show_extra_services_tab.description"),
                            },
                          ].map((option) => (
                            <label
                              key={option.title}
                              className="flex h-full flex-col rounded-lg border border-slate-200 bg-slate-50 p-2 transition hover:border-orange-200"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <p className="text-sm font-black text-slate-800">{option.title}</p>

                                <input
                                  type="checkbox"
                                  checked={option.checked}
                                  disabled={canEdite}
                                  onChange={(e) => option.onChange(e.target.checked)}
                                  className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-500 focus:ring-orange-400"
                                  data-interaction-description-key="interactionDescriptions:pages.myRestaurant.myCompany.publicMenuCustomizationPopup.input.input2"
                                />
                              </div>
                              <p className="mt-2 text-xs leading-relaxed text-slate-500">{option.description}</p>
                            </label>
                          ))}
                        </div>
                      </section>
                    </Animate>

                    <Animate type={AnimationTypes.fadeInTraslate} duration="2.2s">
                      <div className={defaultStyle.divisionBar}></div>
                    </Animate>

                    <Animate type={AnimationTypes.fadeInTraslate} duration="2.2s">
                      <section className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                        <CanView permission={Permissions.CanEditeCompany}>
                          <button
                            type="button"
                            onClick={handleRestoreDefault}
                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-orange-200 bg-white px-5 py-3 text-sm font-black uppercase tracking-[0.14em] text-slate-700 shadow-sm transition hover:border-orange-300 hover:text-orange-600"
                            data-interaction-description-key="interactionDescriptions:pages.myRestaurant.myCompany.publicMenuCustomizationPopup.button.handleRestoreDefault"
                          >
                            <FontAwesomeIcon icon={faArrowsRotate} /> {t("public_menu_customization.restore_default")}
                          </button>
                        </CanView>

                        <CanView permission={Permissions.CanEditeCompany}>
                          <button
                            type="submit"
                            className="inline-flex items-center justify-center rounded-xl bg-emerald-500 px-6 py-3 text-sm font-black uppercase tracking-[0.14em] text-white shadow-lg shadow-emerald-100 transition hover:bg-emerald-600"
                            data-interaction-description-key="interactionDescriptions:pages.myRestaurant.myCompany.publicMenuCustomizationPopup.button.save"
                          >
                            {t("buttons:button_save")}
                          </button>
                        </CanView>

                        {companyMemory.slug.length > 0 && (
                          <Button
                            size="small"
                            typeButton="button"
                            label={t("buttons:button_view_link")}
                            bgColor={THEME.colors.buttonBGRegisterColor}
                            iconColor={THEME.colors.buttonFGSaveColor}
                            onClick={() => {
                              window.open(`${API_ROUTES.SITE}${companyMemory.slug}`, "_blank")
                            }}
                            descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.publicMenuCustomizationPopup.button.viewLink"
                          />
                        )}
                      </section>
                    </Animate>
                  </form>
                </div>
              </Animate>
            </div>
          </Animate>
        </div>
      </ModalPortal>
      {isThemeNamePopupOpen && (
        <PublicMenuThemeNamePopup
          onClose={() => setThemeNamePopupOpen(false)}
          onConfirm={(name) => {
            void saveTheme(companyMemory.id, name, getDesignPayload()).then((theme) => {
              if (theme) {
                setSelectedThemeKey(`custom:${theme.id}`)
                setThemeNamePopupOpen(false)
              }
            })
          }}
        />
      )}
    </>
  )
}
